import { Stack, Typography, Box, Skeleton, CircularProgress, Tooltip, Theme, tooltipClasses, styled, TooltipProps, Paper, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { useState, useMemo, useEffect, useRef } from "react";
import Iconify from "src/components/iconify";
import { setFilterCountry } from "src/context/reducers/filter-settings/filterSlice";
import HorizontalPager from "src/components/horizontal-pager";
import { useGetBQCountriesQuery } from "src/context/api";
import { t } from "i18next";
import { HEADER } from "src/layouts/config-layout";

interface RegionSelectorProps {
    data?: any[];
    loading?: boolean;
    displayKPI?: boolean;
}

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[1],
        fontSize: 11,
    },
}));

// Map region names to country codes
const getCountryCodeFromRegionName = (regionName: string, countries: any[]): string | null => {
    if (!countries || countries.length === 0) return null;

    // Try exact match first
    const exactMatch = countries.find(
        (c) => c.country_name?.toLowerCase() === regionName?.toLowerCase()
    );
    if (exactMatch) return exactMatch.country_code;

    // Try partial match (e.g., "France" matches "France")
    const partialMatch = countries.find(
        (c) => regionName?.toLowerCase().includes(c.country_name?.toLowerCase()) ||
            c.country_name?.toLowerCase().includes(regionName?.toLowerCase())
    );
    if (partialMatch) return partialMatch.country_code;

    // Fallback: try to extract code from region name if it looks like a code
    if (regionName && regionName.length === 2) {
        return regionName.toUpperCase();
    }

    return null;
};


// Component to handle icon loading with loader
function IconifyWithLoader({ icon, width, height }: { icon: string; width: number; height: number }) {
    const [isLoaded, setIsLoaded] = useState(false);
    const iconRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        // Reset loading state when icon changes
        setIsLoaded(false);

        // Check if icon is already loaded
        const checkIconLoaded = () => {
            if (iconRef.current) {
                const svg = iconRef.current.querySelector('svg');
                if (svg) {
                    setIsLoaded(true);
                }
            }
        };

        // Use MutationObserver to detect when the icon SVG is injected into the DOM
        if (iconRef.current) {
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                        checkIconLoaded();
                    }
                });
            });

            observer.observe(iconRef.current, {
                childList: true,
                subtree: true,
            });

            // Also check immediately in case it's already loaded
            checkIconLoaded();

            // Fallback timeout to show icon after 2 seconds even if not detected
            const timeout = setTimeout(() => {
                setIsLoaded(true);
            }, 2000);

            return () => {
                observer.disconnect();
                clearTimeout(timeout);
            };
        }
    }, [icon]);

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'inline-flex',
                width: width,
                height: height,
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {!isLoaded && (
                <CircularProgress
                    size={10}
                    thickness={5}
                    sx={{
                        color: '#919EAB',
                    }}
                />
            )}
            <Box
                ref={iconRef}
                sx={{
                    display: isLoaded ? 'inline-flex' : 'none',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Iconify icon={icon} width={width} height={height} />
            </Box>
        </Box>
    );
}

export default function RegionSelector({ data, loading = false, displayKPI = true }: RegionSelectorProps) {
    const theme = useTheme();
    const filterStore = useSelector((state: RootState) => state.filters);
    const selectedRegion = filterStore?.countries;
    const dispatch = useDispatch();
    const { data: baseCountries, isLoading: isLoadingBaseCountries } = useGetBQCountriesQuery(void 0, {
        skip: data?.every((region) => region.country_code),
    });

    const regionsWithCodes = useMemo(() => {
        if (!data) return [];

        return data
            .map(({ country_code, share_of_brand_searches, perc_click, region_name, country_name }) => {
                const name = region_name || country_name;
                const countryCode = country_code || getCountryCodeFromRegionName(name, baseCountries || []);

                return {
                    region_name: name,
                    countryCode,
                    share_of_brand_searches: share_of_brand_searches || perc_click || 0
                };
            })
            .sort((a, b) => {
                if (a.share_of_brand_searches != null && b.share_of_brand_searches != null) {
                    return b.share_of_brand_searches - a.share_of_brand_searches;
                }
                if (a.share_of_brand_searches == null) return 1;
                if (b.share_of_brand_searches == null) return -1;
                return 0;
            });
    }, [data, baseCountries]);



    useEffect(() => {
        if (regionsWithCodes.length > 0 && (!selectedRegion || selectedRegion.length === 0)) {
            dispatch(setFilterCountry([regionsWithCodes[0].region_name]));
        }
    }, [regionsWithCodes, selectedRegion]);

    if (regionsWithCodes.length <= 1) {
        return null;
    }

    if (loading) {
        return <Box px={"24px"}>
            <Skeleton sx={{ borderRadius: 1 }} variant="rectangular" width="100%" height={40} />
        </Box>;
    }

    return (
        <Paper sx={{ p: 0, overflow: 'hidden', backgroundColor: "white", borderBottom: "1px solid rgba(145, 158, 171, 0.20)", px: '24px', pt: 2, position: 'sticky', top: HEADER.H_DESKTOP + 52, zIndex: theme.zIndex.appBar - 1 }}>
            <HorizontalPager
                spacing={20}
                items={regionsWithCodes}
                getItemKey={(r) => r.region_name}
                activeIndex={selectedRegion ? regionsWithCodes.findIndex(r => r.region_name === selectedRegion[0]) : 0}
                renderItem={(region) => {
                    const isSelected = selectedRegion[0] === region.region_name;

                    return (
                        <Box
                            component="button"
                            key={region.region_name}
                            onClick={() => dispatch(setFilterCountry([region.region_name]))}
                            sx={{
                                appearance: "none",
                                background: "none",
                                border: "none",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                px: 0,
                                py: 0,
                                pb: 1,
                                cursor: "pointer",
                                borderBottom: isSelected ? "2px solid black" : "2px solid transparent",
                                transition: "border-color 0.2s ease",
                                "&:hover": { opacity: 0.8 },
                            }}
                        >
                            <IconifyWithLoader icon={`circle-flags:${region.countryCode}`} width={16} height={16} />
                            <Stack direction="row" px={"6px"} spacing={0.5} alignItems="center">
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 400,
                                        fontSize: "0.875rem",
                                        color: isSelected ? "black" : "#919EAB",
                                    }}
                                >
                                    {region.region_name}
                                </Typography>
                                {displayKPI &&
                                    <LightTooltip
                                        sx={{
                                            [`& .${tooltipClasses.tooltip}`]: (theme: Theme) => ({
                                                backgroundColor: theme.palette.common.white,
                                                color: 'rgba(0, 0, 0, 0.87)',
                                                boxShadow: theme.shadows[1],
                                                fontSize: 11,
                                            }),
                                        }}
                                        title={t('dashboard.global.brandPulse.share_of_brand_searches')}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 400,
                                                fontSize: "0.875rem",
                                                color: "#919EAB",
                                            }}
                                        >
                                            ({region.share_of_brand_searches}%)
                                        </Typography>
                                    </LightTooltip>
                                }
                            </Stack>
                        </Box>
                    );
                }}
            />
        </Paper>
    );
}